'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { StyleSheet, useWindowDimensions } from 'react-native'
import { HelperText } from 'react-native-paper'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { BREAKPOINTS } from '@/lib/config'
import { notify } from '@/lib/notice'
import { useStore } from '@/lib/pocketbase'
import { normalizeTagPayload } from '@/lib/tags'
import { type TagFormData, tagSchema } from '@/types/tags'
import { Card } from '@/ui/Card'
import { FormErrorSummary } from '@/ui/form'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { NotFound } from '@/ui/NotFound'
import { Page } from '@/ui/Page'
import { DeleteTagDialog } from '@/ui/tags/DeleteTagDialog'
import { TagFormFields } from '@/ui/tags/TagFormFields'

export default function EditTagPage() {
    useAvoidSoftInputForScreen()
    const router = useRouter()
    const { navigateBackTo } = useNavigateBack()
    const params = useLocalSearchParams<{ orgSlug: string; tagId: string }>()
    const [tagsCollection] = useStore('tags')
    const [error, _setError] = useState<Error | null>(null)
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deleteError, setDeleteError] = useState<string | null>(null)
    const [isUpdating, setIsUpdating] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const { width } = useWindowDimensions()
    const isWideLayout = width >= BREAKPOINTS.xl

    const { data: tag, isLoading } = useLiveQuery(q =>
        q
            .from({ tags: tagsCollection })
            .where(({ tags }) => eq(tags.id, params.tagId || ''))
            .findOne()
    )

    const form = useForm<TagFormData>({
        resolver: zodResolver(tagSchema),
        defaultValues: {
            name: '',
            color: '#3B82F6',
            description: '',
            inactive: false,
        },
    })

    useEffect(() => {
        if (tag) {
            form.reset({
                name: tag.name,
                color: tag.color ?? '#3B82F6',
                description: tag.description ?? '',
                inactive: Boolean(tag.inactive),
            })
        }
    }, [tag, form])

    const handleSubmit = form.handleSubmit(async data => {
        if (!tag) return
        setIsUpdating(true)
        try {
            const payload = normalizeTagPayload(data)
            const tx = tagsCollection.update(tag.id, draft => {
                Object.assign(draft, payload)
            })
            await tx.isPersisted.promise

            notify.success('Tag updated successfully')
            navigateBackTo(`/${params.orgSlug}/settings/tags`)
        } catch (err) {
            _setError(err instanceof Error ? err : new Error('Failed to update tag'))
        } finally {
            setIsUpdating(false)
        }
    })

    const handleDelete = useCallback(() => {
        setDeleteError(null)
        setDeleteDialogOpen(true)
    }, [])

    const handleConfirmDelete = async () => {
        if (!tag) return
        setIsDeleting(true)
        try {
            setDeleteError(null)
            const tx = tagsCollection.delete(tag.id)
            await tx.isPersisted.promise

            setDeleteDialogOpen(false)
            notify.success('Tag deleted successfully')
            router.replace(`/${params.orgSlug}/settings/tags`)
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete tag'
            setDeleteError(message)
        } finally {
            setIsDeleting(false)
        }
    }

    useHeaderOptions({
        buttons: [
            {
                type: 'delete',
                onPress: handleDelete,
                disabled: isUpdating || isDeleting,
                loading: isDeleting,
                loadingTitle: 'Deleting...',
            },
            {
                type: 'save',
                title: 'Update',
                onPress: handleSubmit,
                loading: isUpdating,
                loadingTitle: 'Updating...',
            },
        ],
        title: tag ? `Edit Tag: ${tag.name}` : undefined,
        enabled: !!tag,
    })

    if (isLoading) {
        return <LoadingIndicator />
    }

    if (!tag) {
        return <NotFound message="Tag not found" />
    }

    return (
        <Page>
            <Card>
                <Card.Content style={styles.cardContent}>
                    <FormErrorSummary
                        errors={form.formState.errors}
                        isEnabled={form.formState.isSubmitted}
                    />
                    <TagFormFields control={form.control} isTwoColumn={isWideLayout} />
                    {error ? <HelperText type="error">{error.message}</HelperText> : null}
                </Card.Content>
            </Card>
            <UnsavedChangesDialog isDirty={form.formState.isDirty} />
            <DeleteTagDialog
                visible={isDeleteDialogOpen}
                tagName={tag.name}
                onDismiss={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                isDeleting={isDeleting}
                errorMessage={deleteError}
            />
        </Page>
    )
}

const styles = StyleSheet.create({
    cardContent: {
        gap: 16,
    },
})
